import { useMemo } from "react";
import { useQuery } from "react-query";

import { getGoalDetails } from "api/goals";
import { STALE_TIME_5_MINUTES, GET_GOAL_BY_ID } from "@constants/reactQueryKeys";
import { normalizeGoal } from "api/adapters/goalAdapters";

/**
 * Custom hook to fetch the details of a specific goal.
 * @param goalId - The ID of the goal to be fetched.
 * @returns An object containing the goal details, loading state, and any error encountered.
 */
const useGetDetailGoal = (goalId: number) => {
  const {
    data: response,
    error,
    isLoading,
    ...rest
  } = useQuery([GET_GOAL_BY_ID, goalId], () => getGoalDetails(goalId), {
    enabled: !!goalId,
    staleTime: STALE_TIME_5_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2, // Retry failed requests up to 2 times
  });

  // normalizeGoal is a TEMPORARY workaround for the BE enum-casing bug
  // (ANOW-6956) — once that ticket is resolved, drop the call (and the
  // goalAdapters import) and return `response?.data || null` directly.
  // Memoized on the cached response so consumers keep referential stability
  // across re-renders instead of receiving a fresh normalized object each time.
  const goal = useMemo(
    () => (response?.data ? normalizeGoal(response.data) : null),
    [response?.data]
  );

  return {
    goal,
    errorGoal: error,
    isGoalLoading: isLoading,
    ...rest,
  };
};

export default useGetDetailGoal;
